<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
            <p class="mb-0 main-identifier fw-semibold">Delegation</p>
            <span class="material-symbols-outlined">keyboard_arrow_right</span>
            <p class="mb-0 sub-identifier">View Delegation Log</p>
        </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Delegation Log</h1>
                <p class="mb-0 small">View and manage delegation logs</p>
              </div>
            </div>

            <div class="form-body">
              <!-- Filters -->
              <div class="filters-section mb-4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Date Initiated:</label>
                    <select class="form-select form-select-sm" v-model="filters.dateInitiated">
                      <option value="">All Time</option>
                      <option value="this_month">This Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Date Completed:</label>
                    <select class="form-select form-select-sm" v-model="filters.dateCompleted">
                      <option value="">All Time</option>
                      <option value="this_month">This Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Status:</label>
                    <select class="form-select form-select-sm" v-model="filters.status">
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  <div class="col-md-3 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="errorMessage" class="alert alert-warning alert-dismissible fade show" role="alert">
                {{ errorMessage }}
                <button type="button" class="btn-close" aria-label="Close" @click.prevent="clearError()"></button>
              </div>

              <!-- Table Controls -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box" style="max-width: 500px;">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search" class="form-control form-control-sm search-input"
                        placeholder="Search by start date, end date or status..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading delegations...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Sender</th>
                        <th>Receiver</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td :colspan="7" class="text-center text-muted py-4">
                          <div v-if="tableNotice">{{ tableNotice }}</div>
                          <div v-else>No delegations to display.</div>
                        </td>
                      </tr>
                      <tr v-for="(delegation, index) in paginatedItems" :key="delegation.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no text-center">
                          <span>{{ (currentPage - 1) * pageLength + index + 1 }}</span>
                        </td>
                        <td data-label="Sender" class="primary-col" title="View Delegation"
                          @click.prevent="viewDelegation(delegation.id)">
                          <router-link
                            :to="{ name: 'ViewDelegation', params: { id: delegation.id } }"
                            class="view-link text-decoration-underline">
                            {{ delegation.senderName || 'N/A' }}
                          </router-link>
                        </td>
                        <td data-label="Receiver" class="secondary-col">{{ delegation.receiverName || 'N/A' }}</td>
                        <td data-label="Start Date" class="secondary-col delegation-date">{{ formatDate(delegation.startDate) }}</td>
                        <td data-label="End Date" class="secondary-col delegation-date">{{ formatDate(delegation.endDate) }}</td>
                        <td data-label="Status" class="secondary-col">{{ getDelegationStatus(delegation) }}</td>
                        <!-- <td data-label="Action" class="action">
                          <a href="#" class="btn btn-sm btn-primary text-decoration-none"
                            @click.prevent="viewDelegation(delegation.id)">
                            View
                          </a>
                        </td> -->
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination Summary and Actions -->
              <div v-if="!loading" class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                    </li>
                    <li v-for="page in totalPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import Cookies from 'js-cookie';
import DashboardLayout from '../layout/DashboardLayout.vue';

import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css' // Import global responsive styles

const router = useRouter()
const delegations = ref([])
const loading = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL
const statusFilter = ref('')
const filters = ref({
  dateInitiated: '',
  dateCompleted: '',
  status: ''
})
const errorMessage = ref('')

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

watch(filters, () => {
  currentPage.value = 1
}, { deep: true })

const fetchDelegations = async () => {
  const token = Cookies.get('auth_token');
  errorMessage.value = ''
  loading.value = true
  try {
    const response = await axios.get(`${root_api_url}/appraisal/delegation/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    delegations.value = response.data || [];
  } catch (error) {
    console.error('Failed to fetch Delegations:', error)
    errorMessage.value = 'Failed to fetch delegations. Please try again.'
  } finally {
    loading.value = false
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}


const getDelegationStatus = (delegation) => {
  if (delegation.isRevoked) return 'Closed'

  return 'Active'
}

// const filteredItems = computed(() => {
//   return delegations.value.filter(delegation => {
//     const term = debouncedSearch.value.toLowerCase();
//     const matchesSearch =
//       delegation.fileTitle.toLowerCase().includes(term) ||
//       delegation.fileNumber.toLowerCase().includes(term) ||
//       String(delegation.createdDate).toLowerCase().includes(term) ||
//       delegation.originatingMda.toLowerCase().includes(term) ||
//       delegation.amountApproved.toString().includes(term) ||
//       delegation.purposeOfRequest.toLowerCase().includes(term);

//     // let matchesStatus = true;

//     // if (statusFilter.value === 'completed') {
//     //   matchesStatus = !delegation.isEditable && !delegation.isRejected && !delegation.isReturned;
//     // } else if (statusFilter.value === 'rejected') {
//     //   matchesStatus = delegation.isRejected === true;
//     // } else if (statusFilter.value === 'returned') {
//     //   matchesStatus = delegation.isReturned === true;
//     // } else if (statusFilter.value === 'pending') {
//     //   matchesStatus = delegation.isEditable === true && !delegation.isRejected && !delegation.isReturned && !delegation.hasAppraisal;
//     // } else if (statusFilter.value === 'appraisal_initiated') {
//     //   matchesStatus = delegation.hasAppraisal === true;
//     // }

//     const status = getDelegationStatus(delegation).toLowerCase().replace(' ', '_')

//     const matchesStatus =
//       !statusFilter.value || status === statusFilter.value

//     return matchesSearch && matchesStatus;
//   }) .sort((a, b) => {
//       const timeA = new Date(a.createdDate).getTime() || 0
//       const timeB = new Date(b.createdDate).getTime() || 0
//       return timeB - timeA
//     });

//      // Apply status filter
//   if (filters.value.status) {
//     filtered = filtered.filter(w => w.status.toLowerCase() === filters.value.status.toLowerCase())
//   }
// });

const filteredItems = computed(() => {
  let filtered = delegations.value

  // Apply search filter
  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    filtered = filtered.filter(delegation => {
      return (
        String(delegation.createdDate).toLowerCase().includes(term) ||
        String(delegation.completedDate).toLowerCase().includes(term) ||
        delegation.status.toString().includes(term)
      )
    })
  }

  // Apply date filter
  if (filters.value.date) {
    const now = new Date()
    filtered = filtered.filter(w => {
      const createdDate = new Date(w.createdDate)
      switch (filters.value.date) {
        case 'this_month':
          return createdDate.getMonth() === now.getMonth() &&
            createdDate.getFullYear() === now.getFullYear()
        case 'last_month': {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1)
          return createdDate.getMonth() === lastMonth.getMonth() &&
            createdDate.getFullYear() === lastMonth.getFullYear()
        }
        case 'this_year':
          return createdDate.getFullYear() === now.getFullYear()
        default:
          return true
      }
    })
  }

  // Apply date sorting (MOST RECENT FIRST)
  filtered = [...filtered].sort((a, b) => {
    return new Date(b.createdDate) - new Date(a.createdDate)
  })

  // Apply status filter
  if (filters.value.status) {
    filtered = filtered.filter(w => getDelegationStatus(w).toLowerCase() === filters.value.status.toLowerCase())
  }

  return filtered
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

watch(statusFilter, () => {
  currentPage.value = 1
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const clearFilters = () => {
  filters.value = {
    mda: '',
    date: '',
    status: ''
  }
  search.value = ''
}

const viewDelegation = (delegationId) => {
  if (!delegationId) {
    errorMessage.value = 'Unable to open delegation details: delegation ID is missing.'
    return
  }
  router.push({ name: 'ViewDelegation', params: { id: delegationId } })
}

const clearError = () => {
  errorMessage.value = ''
}

const exportToPDF = () => {
  const doc = new jsPDF()
  autoTable(doc, {
    head: [['S/N', 'File Title', 'File Number', 'Created Date', 'Originating MDA', 'Amount Approved (₦)', 'Purpose of Request', 'Status']],
    body: delegations.value.map((delegation, index) => [
      index + 1,
      delegation.fileTitle,
      delegation.fileNumber,
      delegation.createdDate,
      delegation.originatingMda,
      delegation.amountApproved,
      delegation.purposeOfRequest,
      getDelegationStatus(delegation)
    ])
  })
  doc.save('Delegations.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('delegations')
  worksheet.addRow(['S/N', 'File Title', 'File Number', 'Created Date', 'Originating MDA', 'Amount Approved (₦)', 'Purpose of Request', 'Status'])

  delegations.value.forEach((delegation, index) => {
    worksheet.addRow([
      index + 1,
      delegation.fileTitle,
      delegation.fileNumber,
      delegation.createdDate,
      delegation.originatingMda,
      delegation.amountApproved,
      delegation.purposeOfRequest,
      getDelegationStatus(delegation)
    ])
  })

  worksheet.getRow(1).font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'delegations.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
    await fetchDelegations(),
    document.title = "View Delegations"
});

</script>

<style scoped>
/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles */
.delegation-date {
  min-width: 150px;
}

.primary-col {
  font-weight: 500;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}
</style>
